import { useGetClientByIdQuery, useUpdateClientMutation } from '@entities/clients/rtk-query.ts';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useMemo, useState } from 'react';
import { useClientContactModal } from '@widgets/clients/client_contact_modal/helpers.ts';
import { contactIconsMap, type ContactType } from '@widgets/contacts/contact-edit/constans.tsx';
import { doTransformRTKErrorResponse, isPhoneNumber } from '@shared/helpers/index.ts';
import { usePopup } from '@shared/hooks/index.ts';
import type { ContactItem } from '@shared/types/contacts';
import { DropdownItem, useMenuDropdown } from '@shared/ui/index.ts';
import { DropdownMenuWithButton } from '@shared/ui/menus/dropdown-menu/index.ts';
import { TextOverflow } from '@shared/ui/TextOverflow/index.tsx';
import { Loader } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';

type PT = {
  id: number;
};
export const ContactsList = ({ id }: PT) => {
  const { data: client, isLoading: isClientLoading } = useGetClientByIdQuery(id);
  const { openClientContactModal } = useClientContactModal();
  const { popup } = usePopup();
  const [contactIdInProgress, setContactIdInProgress] = useState(-1);

  const [updateClientMutation, { isLoading: isClientUpdating }] = useUpdateClientMutation();

  const handleClickEdit = (contact: ContactItem) => {
    openClientContactModal({
      contactData: contact,
      type: 'edit',
      clientId: client?.id,
    });
  };

  const handleClickDelete = async (contact: ContactItem) => {
    if (client) {
      setContactIdInProgress(contact.id);
      const newActiveContacts = client?.active_contacts
        ?.filter((c) => c.id !== contact.id)
        .map((c) => ({
          ...c,
          user: undefined,
        }));
      const formData = new FormData();
      formData.append(`active_contacts`, JSON.stringify(newActiveContacts));
      const resp = await updateClientMutation({ body: formData, id });
      if (resp?.error) {
        const transformedError = doTransformRTKErrorResponse(resp?.error as FetchBaseQueryError);
        const errMessage = transformedError?.firstError;
        errMessage && popup({ type: 'error', text: errMessage });
      } else {
        setContactIdInProgress(-1);
        popup({ type: 'success', text: 'Контакт успешно удалён!' });
      }
    }
  };

  return useMemo(() => {
    if (isClientLoading) {
      return <Loader />;
    }
    if (client) {
      if (client?.active_contacts || client?.phonenumber || client?.email) {
        const { active_contacts, phonenumber, email } = client;
        const fakeData = {
          id: 1,
          user: 1,
          base_url: 'awd',
          formated_phone: '123',
          description: '123',
        };
        return (
          <ul className={st.list}>
            {phonenumber && (
              <Contact
                contact={{ type: 'phone', value: phonenumber, ...fakeData }}
                withDropdown={false}
              />
            )}
            {email && (
              <Contact
                contact={{ type: 'email', value: email, ...fakeData }}
                withDropdown={false}
              />
            )}
            {active_contacts &&
              active_contacts.map((contact) => (
                <Contact
                  key={contact.id}
                  contact={contact}
                  handleDelete={handleClickDelete}
                  handleEdit={handleClickEdit}
                  isDeleting={contact.id === contactIdInProgress && isClientUpdating}
                />
              ))}
          </ul>
        );
      } else {
        return 'У данного клиента нет контактов';
      }
    } else {
      return 'Нет выбранного клиента';
    }
  }, [client, isClientLoading]);
};

type ContactPT = {
  handleEdit?: (contact: ContactItem) => void;
  handleDelete?: (contact: ContactItem) => void;
  contact: ContactItem;
  withDropdown?: boolean;
  isDeleting?: boolean;
};
const Contact = (props: ContactPT) => {
  const { contact, handleDelete, handleEdit, withDropdown = true, isDeleting = false } = props;
  const { value, type } = contact;
  const { menuRef, handleAction, handleToggleDropdown, isMenuOpen } = useMenuDropdown({});
  return (
    <li className={st.contact}>
      <div className={st.icon}>{contactIconsMap[type as ContactType]}</div>
      <TextOverflow className={st.value}>{isPhoneNumber(value) ? `+${value}` : value}</TextOverflow>
      {withDropdown && (
        <DropdownMenuWithButton
          isMenuOpen={isMenuOpen}
          onMenuBtnClick={handleToggleDropdown}
          menuRef={menuRef}
        >
          <DropdownItem onClick={handleAction(() => handleEdit?.(contact))}>Изменить</DropdownItem>
          <DropdownItem onClick={handleAction(() => handleDelete?.(contact))} disabled={isDeleting}>
            Удалить
          </DropdownItem>
        </DropdownMenuWithButton>
      )}
    </li>
  );
};
